import generatePassword from "generate-password";
import { observer } from "mobx-react-lite";
import { useContext } from "react";

import { dataAttribute, DataAttributes, Heading } from "@bps/fluent-ui";
import { ExportPatientRecordModalFormFields } from "@modules/practice/screens/shared-components/export-patient-record-modal/ExportPatientRecordModalFormFields.tsx";
import { ExportPatientRecordFormValues } from "@shared-types/practice/export-patient-record-form-values.types.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { SubmissionFormDialog } from "@ui-components/form/submission-form-dialog/SubmissionFormDialog.tsx";
import { usePatientLabel } from "@ui-components/hooks/usePatientLabel.ts";

import { ExportPatientRecordContext } from "./context/ExportPatientRecordContext.ts";

export const ExportPatientRecordModal = observer(() => {
  const { practice, notification } = useStores();

  const {
    masterPatient,
    setShowExportPatientRecordModal,
    setExportRecordValues,
    setShowExportPatientRecordPasswordDialog
  } = practice.ui;

  const { scheduleExportPatientRecordJob } = useContext(
    ExportPatientRecordContext
  );

  const patientLabelCapitalised = usePatientLabel(true);
  const patientLabel = usePatientLabel();

  const patientId = masterPatient?.id;

  const closeModal = () => setShowExportPatientRecordModal(false);
  const onSubmit = async (values: ExportPatientRecordFormValues) => {
    if (patientId) {
      const password = generatePassword.generate({
        length: 10,
        numbers: true,
        symbols: true,
        uppercase: true,
        lowercase: true,
        strict: true
      });

      setExportRecordValues({ ...values, password });

      notification.warn(`${patientLabelCapitalised} export underway`);

      await scheduleExportPatientRecordJob(patientId);

      setShowExportPatientRecordPasswordDialog(true);
    } else {
      notification.error(
        `${patientLabelCapitalised} ID is not found. Please contact support and provide the ${patientLabel} name.`
      );
      throw Error(
        "Patient ID is not provided. Export patient record cannot be initiated."
      );
    }

    closeModal();
  };

  return (
    <SubmissionFormDialog<ExportPatientRecordFormValues>
      dialogName={`Export ${patientLabel} ${masterPatient?.fullName}`}
      dialogProps={{
        onDismiss: closeModal,
        minWidth: 600,
        dialogContentProps: {
          title: (
            <Heading variant="modal-heading">
              {`Export ${patientLabel} ${masterPatient?.fullName}`}
            </Heading>
          )
        }
      }}
      onSubmitSucceeded={closeModal}
      onSubmit={onSubmit}
      buttonsProps={{
        submitButtonProps: {
          text: "Export",
          iconProps: { hidden: true }
        },
        cancelButtonProps: {
          ...dataAttribute(DataAttributes.Element, "export-cancel-button")
        },
        disableSubmitOnPristine: false
      }}
    >
      {() => <ExportPatientRecordModalFormFields />}
    </SubmissionFormDialog>
  );
});
